import { Kbd, ListItem, UnorderedList } from '@chakra-ui/react';
import { useChartDownload } from '@resistapp/client/contexts/chart-download-context';
import { ContextualTipId, useContextualTipContext } from '@resistapp/client/contexts/contextual-tip-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { getLegendNormalisationDescription } from '@resistapp/common/normalisation-mode';
import { FullProject, FullSample } from '@resistapp/common/types';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { scaleOrdinal } from '@visx/scale';
import { isWithinInterval } from 'date-fns';
import { chain, Dictionary } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ChartHotkeyInstructions } from '../../tooltips/chart-hotkey-instructions';
import { getSampleSelector, VisualSelectionStatus } from '../bar-box-map/utils';
import { BaseLegend } from './base-legend';
import { KeysPressOptions, Legend } from './legend';

interface SampleLegendProps {
  toggleSamplingPoint: (envId: number | number[], removeOldSelections: boolean) => void;
  filters: Filters;
  project: FullProject;
  legendHeight: string;
  id?: string;
  showSampleNumbering: boolean;
  /** Ref for first label (sample) in legend for contextual tips */
  firstLabelRef?: React.RefObject<HTMLElement>;
  /** Ref for second label */
  secondLabelRef?: React.RefObject<HTMLElement>;
}

export function SampleLegend(props: SampleLegendProps) {
  const { project, filters, toggleSamplingPoint, showSampleNumbering } = props;
  const { firstLabelRef, secondLabelRef } = props;
  const {
    samplesBeingSelected,
    setSamplesBeingSelected,
    focusedSamplesBySmplPntId,
    samplingPointsForSelectedGroup,
    samplingPointIdsForSelectedGroup,
    adminFeatures,
    sequentialSampleIndexes,
    queryFilters,
    smplPntColorById: envColorById,
    getVisualSelectionStatus,
  } = useResearchContext();
  const nameById = useMemo(
    () =>
      chain(samplingPointsForSelectedGroup)
        .keyBy(point => point.id)
        .mapValues(point => point.name)
        .value(),
    [samplingPointsForSelectedGroup],
  );
  const normalisationMode = useNormalisationMode();
  const { downloadChartAndLegend } = useChartDownload();
  const { markInteraction, setTipEnabled } = useContextualTipContext();

  const activeKeys = useRef<KeysPressOptions | undefined>();
  // The first sample of the first replicate group of each sampling point, in project sample order
  const firstSampleBySmplPntId = useMemo(
    () => getFirstSampleBySamplingPointId(project.samplesByUID),
    [project.samplesByUID],
  );
  // Create a mapping of sampling point ID to sample for sorting
  const sampleAndSortIndexBySmplPntId = useMemo(() => {
    return chain(samplingPointsForSelectedGroup)
      .keyBy(point => point.id)
      .mapValues(point => {
        const sample = firstSampleBySmplPntId.get(point.id);
        if (!sample) {
          return null;
        }
        const sortIndex = sequentialSampleIndexes[sample.id];
        return { sample, sortIndex };
      })
      .pickBy() // Remove null values
      .value() as Dictionary<{
      sample: FullSample;
      sortIndex: number;
    }>;
  }, [samplingPointsForSelectedGroup, firstSampleBySmplPntId, sequentialSampleIndexes]);

  // Sort the sampling point IDs by their samples' sort indexes
  const sortedSamplingPointIds = useMemo(() => {
    // Convert samplingPointIds to an array of objects with id and sortIndex
    const pointsWithIndex = samplingPointsForSelectedGroup.map(point => ({
      id: point.id,
      sortIndex: sampleAndSortIndexBySmplPntId[point.id].sortIndex || 999999,
    }));

    // Sort by sortIndex and extract just the ids
    return pointsWithIndex.sort((a, b) => a.sortIndex - b.sortIndex).map(item => item.id);
  }, [samplingPointsForSelectedGroup, sampleAndSortIndexBySmplPntId]);

  const sampleNumberCountBySmplPntId = useMemo(
    () => getUniqueSampleNumberCountBySamplingPointId(project.samplesByUID),
    [project.samplesByUID],
  );
  const showSampleNumbers = useMemo(() => {
    return (
      showSampleNumbering &&
      !samplingPointsForSelectedGroup.find(
        point =>
          // Sampling point has more than one sample number
          (sampleNumberCountBySmplPntId.get(point.id) ?? 0) > 1,
      )
    );
  }, [samplingPointsForSelectedGroup, showSampleNumbering, sampleNumberCountBySmplPntId]);

  const sampleLabelBySmplPntId = useMemo(
    () =>
      chain(samplingPointsForSelectedGroup)
        .keyBy(point => `${point.id}`)
        .mapValues(point => {
          if (!showSampleNumbers) {
            return point.name;
          } else {
            const sample = firstSampleBySmplPntId.get(point.id);
            return `${sample?.number} - ${point.name}`;
          }
        })
        .value(),
    [samplingPointsForSelectedGroup, showSampleNumbers, firstSampleBySmplPntId],
  );

  // Use sortedSamplingPointIds instead of samplingPointIds for palette
  const palette = sortedSamplingPointIds.map(id => envColorById[Number(id)]);
  const scale = scaleOrdinal<string, string>({
    domain: sortedSamplingPointIds.map(String),
    range: palette,
  });

  const { selectedSamplingPointIdsOrdered } = filters;

  // Determine which sampling point IDs have data within the current time interval
  const samplingPointIdsWithDataInTimeRange = useMemo(() => {
    const result = new Set<string>();

    Object.values(project.samplesByUID).forEach(samples => {
      const sample = samples[0];
      // Samples without a timestamp should still be considered "in range" so that their
      // legend entries are not dimmed. Treat them as always within the currently selected
      // interval. When a timestamp exists, include the sampling point only if the sample is
      // inside the active time window.
      if (!sample.time || isWithinInterval(new Date(sample.time), filters.interval)) {
        result.add(String(sample.samplingPoint.id));
      }
    });

    return result;
  }, [project.samplesByUID, filters.interval]);

  const getOpacity = useCallback(
    (_envId: string | number) => {
      const samplingPointId = +_envId;
      const visualStatus = getVisualSelectionStatus(samplingPointId);

      // Also check if the sampling point has data in the current time range
      const hasDataInTimeRange = samplingPointIdsWithDataInTimeRange.has(String(samplingPointId));

      // Legend items are dimmed if deselected OR pending removal OR if they have no data in the current range
      return visualStatus === VisualSelectionStatus.DESELECTED ||
        visualStatus === VisualSelectionStatus.PENDING_REMOVE ||
        !hasDataInTimeRange
        ? 0.4
        : 1;
    },
    [getVisualSelectionStatus, samplingPointIdsWithDataInTimeRange],
  );

  const onClick = useCallback(
    (envId: number | string, keyPresOpts: KeysPressOptions) => {
      const selector = getSampleSelector(
        selectedSamplingPointIdsOrdered,
        samplingPointIdsForSelectedGroup,
        samplesBeingSelected,
        setSamplesBeingSelected,
        toggleSamplingPoint,
        true,
        false,
        filters,
        queryFilters.setIntervalStable,
        project,
      );
      selector(envId, keyPresOpts);
      activeKeys.current = keyPresOpts;

      // Record interactions for contextual tips
      if (keyPresOpts.ctrl || keyPresOpts.shift) {
        markInteraction(ContextualTipId.RESEARCH_SAMPLE_LEGEND_MULTI);
      } else {
        markInteraction(ContextualTipId.RESEARCH_SAMPLE_LEGEND_SELECT);
      }
    },
    [
      filters,
      project,
      samplingPointIdsForSelectedGroup,
      toggleSamplingPoint,
      samplesBeingSelected,
      setSamplesBeingSelected,
      selectedSamplingPointIdsOrdered,
      queryFilters.setIntervalStable,
      markInteraction,
    ],
  );
  const labelFormat = (envId: string | number) =>
    showSampleNumbering ? sampleLabelBySmplPntId[envId] : nameById[envId];
  const getSamplesBySmplPntId = (label: string | number) => focusedSamplesBySmplPntId[Number(label)];
  const { description, valueTip } = getLegendNormalisationDescription(normalisationMode);

  // Update focusedSamplingPointIds to only include sampling point IDs that have data in the time range
  const focusedSamplingPointIds = useMemo(() => {
    const baseIds =
      selectedSamplingPointIdsOrdered.length > 0 ? selectedSamplingPointIdsOrdered : sortedSamplingPointIds;

    // Include all IDs, but client code will use the opacity function to gray out those without data
    return baseIds;
  }, [selectedSamplingPointIdsOrdered, sortedSamplingPointIds]);

  // Get all unique statuses from focused samples to determine initial status
  const sampleStatus = useMemo(() => {
    const focusedSamples = Object.values(props.project.focusedByUID || {}).flat();
    const uniqueStatuses = [...new Set(focusedSamples.map(s => s.status))];
    return uniqueStatuses.length === 1 ? uniqueStatuses[0] : ' ';
  }, [props.project.focusedByUID]);
  const downloadTooltip = 'Download image of the Samples chart or legend.';

  // Enable or disable sample legend tips based on available samples
  useEffect(() => {
    const sampleCount = sortedSamplingPointIds.length;
    setTipEnabled(ContextualTipId.RESEARCH_SAMPLE_LEGEND_SELECT, sampleCount >= 1);
    setTipEnabled(ContextualTipId.RESEARCH_SAMPLE_LEGEND_MULTI, sampleCount >= 2);
  }, [sortedSamplingPointIds, setTipEnabled]);

  return (
    <BaseLegend
      id={props.id}
      header="Samples"
      description={description}
      optionMinimum={2}
      TooltipContent={
        <div>
          {valueTip ? <div style={{ marginBottom: '10px' }}>{valueTip}</div> : null}
          <UnorderedList style={{ marginBottom: '20px' }}>
            <ListItem>
              <Kbd>Click</Kbd> a label or chart bar to select a sample
            </ListItem>
            <ChartHotkeyInstructions label="samples" />
          </UnorderedList>
        </div>
      }
      sampleStatuses={
        adminFeatures.isModifyAsAdminActive
          ? {
              update: adminFeatures.updateSampleStatuses,
              status: sampleStatus,
            }
          : undefined
      }
      canUpdateSampleStatuses={adminFeatures.isModifyAsAdminActive}
      onDownload={
        props.id
          ? () => {
              downloadChartAndLegend({
                legendId: props.id,
                chartId: 'research-view-samples-chart',
                plotType: 'box',
                label: 'Samples',
              });
            }
          : undefined
      }
      downloadTooltip={downloadTooltip}
    >
      <Legend
        colorScale={scale}
        labelFormat={labelFormat}
        getOpacity={getOpacity}
        onClick={onClick}
        height={props.legendHeight}
        getSamplesBySmplPntId={adminFeatures.isModifyAsAdminActive ? getSamplesBySmplPntId : undefined}
        focusedSamplingPointIds={focusedSamplingPointIds}
        updateSampleStatuses={adminFeatures.updateSampleStatuses}
        filters={filters}
        firstLabelRef={firstLabelRef}
        secondLabelRef={secondLabelRef}
      />
    </BaseLegend>
  );
}

// The first sample of the first replicate group (in samplesByUID key order) for each sampling point
function getFirstSampleBySamplingPointId(samplesByUID: FullProject['samplesByUID']): Map<number, FullSample> {
  const firstSampleBySmplPntId = new Map<number, FullSample>();
  Object.keys(samplesByUID).forEach(uid => {
    const sample = samplesByUID[uid][0];
    if (!firstSampleBySmplPntId.has(sample.samplingPoint.id)) {
      firstSampleBySmplPntId.set(sample.samplingPoint.id, sample);
    }
  });
  return firstSampleBySmplPntId;
}

function getUniqueSampleNumberCountBySamplingPointId(samplesByUID: FullProject['samplesByUID']): Map<number, number> {
  const numbersBySmplPntId = new Map<number, Set<number>>();
  flattenSamplesByUID(samplesByUID).forEach(sample => {
    const samplingPointId = sample.samplingPoint.id;
    const numbers = numbersBySmplPntId.get(samplingPointId) ?? new Set<number>();
    numbers.add(sample.number);
    numbersBySmplPntId.set(samplingPointId, numbers);
  });
  return new Map(
    Array.from(numbersBySmplPntId.entries(), ([samplingPointId, numbers]) => [samplingPointId, numbers.size]),
  );
}
